import React, { useEffect, useState } from "react";
import { CloudOff, RefreshCw, CloudUpload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { flushQueue, isOnline, subscribePending } from "@/lib/offline/queue";

interface Props {
  /** Anropas när kön synkats så att vyn kan läsa om data. */
  onSynced?: () => void;
}

export const OfflineBanner: React.FC<Props> = ({ onSynced }) => {
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => subscribePending(setPending), []);

  useEffect(() => {
    const goOnline = async () => {
      setOnline(true);
      const synced = await flushQueue();
      if (synced > 0) {
        toast.success(`${synced} ändringar synkade`);
        onSynced?.();
      }
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    // Försök tömma kön direkt vid montering
    void goOnline();
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncNow = async () => {
    setSyncing(true);
    const synced = await flushQueue();
    setSyncing(false);
    if (synced > 0) {
      toast.success(`${synced} ändringar synkade`);
      onSynced?.();
    } else {
      toast.info("Inget att synka");
    }
  };

  if (online && pending === 0) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-2">
        {online ? (
          <CloudUpload className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        ) : (
          <CloudOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        )}
        <div>
          <p className="font-medium">
            {online ? "Ändringar väntar på synk" : "Offline – du kan fortsätta arbeta"}
          </p>
          <p className="text-muted-foreground">
            {pending > 0
              ? `${pending} ändring${pending === 1 ? "" : "ar"} sparade i mobilen och skickas upp automatiskt när nätet är tillbaka.`
              : "Allt du fyller i sparas i mobilen och skickas upp automatiskt när nätet är tillbaka."}
          </p>
        </div>
      </div>
      {online && pending > 0 && (
        <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing}>
          <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
          Synka nu
        </Button>
      )}
    </div>
  );
};
